import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { get } from 'lodash'
import moment from 'moment-timezone'
import { useHistory } from 'react-router-dom'

import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import LoadingSpinner from '../leevo_ui/LoadingSpinner/LoadingSpinner'
import Avatar from '../leevo_ui/Avatar/Avatar'
import useFacility from '../../utilities/hooks/useFacility'
import { requestCreator } from '../../utilities/requests'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs'
import { Textarea } from '../ui/textarea'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from '../ui/drawer'
import SkillButton from '../leevo_ui/SkillButton/SkillButton'
import {
  ScrollableTableContainer,
  ScrollableTable,
  ScrollableTableHeader,
  ScrollableTableBody,
  ScrollableTableHead,
  ScrollableTableRow,
  ScrollableTableCell
} from '../ui/scrollable-table'
import { getNextSkillStatus, updateStudentSkillStatus } from '../../utilities/studentsSkills'
import { hasDistinctStudentLegalName, studentDisplayName } from '../../utilities/studentNames'

const { get: getDashboard, cancel: cancelDashboardRequest } = requestCreator()
const { get: getSkillLevels, cancel: cancelSkillLevelsRequest } = requestCreator()
const { get: getStudentNotes } = requestCreator()
const NOTE_VISIBILITIES = ['public', 'private']

function FacilitiesCoachesDashboard() {
  const { facility, isLoading: isFacilityLoading } = useFacility({ isSubscriptionRequired: false })
  const { replace: redirect } = useHistory()
  const [students, setStudents] = useState([])
  const [skillLevels, setSkillLevels] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStudentId, setSelectedStudentId] = useState(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [notes, setNotes] = useState([])
  const [isNotesLoading, setIsNotesLoading] = useState(false)
  const [noteTab, setNoteTab] = useState('public')
  const [noteDraftsByVisibility, setNoteDraftsByVisibility] = useState({ public: '', private: '' })
  const [activeEditByVisibility, setActiveEditByVisibility] = useState({ public: null, private: null })
  const [noteErrorsByVisibility, setNoteErrorsByVisibility] = useState({ public: '', private: '' })
  const [levelErrors, setLevelErrors] = useState('')
  const [isSavingLevel, setIsSavingLevel] = useState(false)
  const [isSavingNote, setIsSavingNote] = useState(false)
  const [skillErrorMessage, setSkillErrorMessage] = useState('')
  const [updatingSkillIds, setUpdatingSkillIds] = useState([])

  useEffect(() => {
    if (isFacilityLoading || !facility?.id) return

    setIsLoading(true)
    Promise.all([
      getDashboard(`/api/facilities/${facility.id}/coaches_dashboard`),
      getSkillLevels(`/api/facilities/${facility.id}/skill_levels`)
    ])
      .then(([dashboardStudents, levels]) => {
        setStudents(dashboardStudents || [])
        setSkillLevels(levels || [])
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          const status = get(error, 'response.status')
          if (status === 401) {
            redirect('/login')
          } else if (status === 403) {
            redirect('/404')
          } else {
            redirect('/500')
          }
        }
      })
      .finally(() => {
        setIsLoading(false)
      })

    return () => {
      cancelDashboardRequest()
      cancelSkillLevelsRequest()
    }
  }, [facility?.id, isFacilityLoading, redirect])

  const selectedStudent = useMemo(
    () => students.find((student) => String(student.id) === String(selectedStudentId)) || null,
    [students, selectedStudentId]
  )

  const filteredStudents = useMemo(() => {
    const term = searchQuery.toLowerCase()
    return students
      .filter((student) => {
        if (!term.trim()) return true
        const fields = [
          student.fullName,
          student.preferredName,
          student.email,
          student.guardianEmail,
          student.currentSkillLevelName,
          ...(student.classNames || [])
        ]
        return fields.some((field) =>
          String(field || '')
            .toLowerCase()
            .includes(term)
        )
      })
      .sort((a, b) => studentDisplayName(a).localeCompare(studentDisplayName(b), 'en', { sensitivity: 'base' }))
  }, [students, searchQuery])

  const notesByVisibility = useMemo(
    () =>
      NOTE_VISIBILITIES.reduce((memo, visibility) => {
        memo[visibility] = notes.filter((note) => note.visibility === visibility)
        return memo
      }, {}),
    [notes]
  )

  const visibleNoteDraft = noteDraftsByVisibility[noteTab]
  const visibleNoteError = noteErrorsByVisibility[noteTab]
  const visibleEditId = activeEditByVisibility[noteTab]
  const visibleNotes = notesByVisibility[noteTab] || []

  function openStudentPanel(studentId) {
    setSelectedStudentId(studentId)
    setIsDrawerOpen(true)
    setLevelErrors('')
    setNoteTab('public')
    setNoteDraftsByVisibility({ public: '', private: '' })
    setActiveEditByVisibility({ public: null, private: null })
    setNoteErrorsByVisibility({ public: '', private: '' })
    setSkillErrorMessage('')
    setUpdatingSkillIds([])
    loadStudentNotes(studentId)
  }

  function closeStudentPanel() {
    setIsDrawerOpen(false)
    setSelectedStudentId(null)
    setNotes([])
    setNoteTab('public')
    setNoteDraftsByVisibility({ public: '', private: '' })
    setActiveEditByVisibility({ public: null, private: null })
    setNoteErrorsByVisibility({ public: '', private: '' })
    setLevelErrors('')
    setSkillErrorMessage('')
    setUpdatingSkillIds([])
  }

  function loadStudentNotes(studentId) {
    if (!facility?.id || !studentId) return

    setIsNotesLoading(true)
    return getStudentNotes(`/api/facilities/${facility.id}/student_notes`, { params: { student_id: studentId } })
      .then((records) => {
        setNotes(records || [])
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          setNoteErrorsByVisibility((previous) => ({
            ...previous,
            [noteTab]: 'Could not load notes right now. Try again.'
          }))
        }
        throw error
      })
      .finally(() => {
        setIsNotesLoading(false)
      })
  }

  function formatEditedBy(user) {
    if (!user) return 'Unknown'
    return user.preferredName || user.fullName || 'Unknown'
  }

  function formatTimestamp(timestamp) {
    if (!timestamp) return 'Not available'
    const zone = facility?.timeZone || moment.tz.guess()
    return moment(timestamp).tz(zone).format('M/D @ h:mmA z')
  }

  function formatUpdatedByText(user, timestamp) {
    if (!timestamp) return 'No recent update'
    return `Updated by ${formatEditedBy(user)} on ${formatTimestamp(timestamp)}`
  }

  async function handleLevelChange(studentId, nextLevelId) {
    if (!facility?.id) return
    if (String(selectedStudent?.currentSkillLevelId || '') === String(nextLevelId)) return
    setIsSavingLevel(true)
    setLevelErrors('')

    try {
      await axios.patch(`/api/facilities/${facility.id}/students/${studentId}`, {
        student: { current_skill_level_id: nextLevelId }
      })

      const dashboardStudents = await getDashboard(`/api/facilities/${facility.id}/coaches_dashboard`)
      setStudents(dashboardStudents || [])
    } catch (error) {
      setLevelErrors('Could not update level. Try again.')
    } finally {
      setIsSavingLevel(false)
    }
  }

  async function saveNote() {
    if (!selectedStudent?.id || !facility?.id) return
    if (!visibleNoteDraft.trim()) {
      setNoteErrorsByVisibility((previous) => ({ ...previous, [noteTab]: 'Note text is required.' }))
      return
    }

    setNoteErrorsByVisibility((previous) => ({ ...previous, [noteTab]: '' }))
    setIsSavingNote(true)
    const payload = {
      student_note: {
        body: visibleNoteDraft.trim(),
        visibility: noteTab
      },
      student_id: selectedStudent.id
    }

    try {
      if (visibleEditId) {
        await axios.patch(`/api/facilities/${facility.id}/student_notes/${visibleEditId}`, payload, {
          params: { student_id: selectedStudent.id }
        })
      } else {
        await axios.post(`/api/facilities/${facility.id}/student_notes`, payload)
      }
      await loadStudentNotes(selectedStudent.id)
      setNoteDraftsByVisibility((previous) => ({ ...previous, [noteTab]: '' }))
      setActiveEditByVisibility((previous) => ({ ...previous, [noteTab]: null }))
    } catch (error) {
      const serverErrors = get(error, 'response.data.errors')
      setNoteErrorsByVisibility((previous) => ({
        ...previous,
        [noteTab]: serverErrors?.[0] || 'Could not save note. Try again.'
      }))
    } finally {
      setIsSavingNote(false)
    }
  }

  function isSkillLoading(skillId) {
    return updatingSkillIds.includes(String(skillId))
  }

  async function handleSkillChange(skill) {
    if (!selectedStudent?.id) return

    const skillId = String(skill.id)
    const nextStatus = getNextSkillStatus(skill.status)
    setSkillErrorMessage('')
    setUpdatingSkillIds((previous) => [...previous, skillId])

    try {
      const updatedSkill = await updateStudentSkillStatus(skill.id, nextStatus)
      setStudents((previous) =>
        previous.map((student) => {
          if (String(student.id) !== String(selectedStudent.id)) return student
          return {
            ...student,
            studentsSkills: (student.studentsSkills || []).map((record) =>
              String(record.id) === String(updatedSkill.id)
                ? {
                    ...record,
                    status: updatedSkill.status,
                    statusUpdatedAt: updatedSkill.statusUpdatedAt,
                    statusUpdatedBy: updatedSkill.statusUpdatedBy
                  }
                : record
            )
          }
        })
      )
    } catch (error) {
      setSkillErrorMessage('Could not update skill. Try again.')
    } finally {
      setUpdatingSkillIds((previous) => previous.filter((id) => id !== skillId))
    }
  }

  if (isLoading) {
    return (
      <ApplicationLayout data-cy="coaches-dashboard-loading">
        <LoadingSpinner size="1/5" className="flex items-center justify-center m-10" />
      </ApplicationLayout>
    )
  }

  return (
    <ApplicationLayout data-cy="coaches-dashboard">
      <Drawer open={isDrawerOpen} onOpenChange={(nextOpen) => (nextOpen ? null : closeStudentPanel())}>
        <div className="px-4 sm:px-6 lg:px-8 pt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-2xl font-semibold text-gray-900">Coaches Dashboard</h1>
            <div className="w-full sm:max-w-md">
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search students, classes, levels, or guardian email"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-600"
              />
            </div>
          </div>
        </div>

        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <ScrollableTableContainer className="hidden lg:block" height={650}>
            <ScrollableTable>
              <ScrollableTableHeader>
                <ScrollableTableRow>
                  <ScrollableTableHead>Student</ScrollableTableHead>
                  <ScrollableTableHead>Classes</ScrollableTableHead>
                  <ScrollableTableHead>Level</ScrollableTableHead>
                  <ScrollableTableHead>Guardian Email</ScrollableTableHead>
                  <ScrollableTableHead className="text-right">Actions</ScrollableTableHead>
                </ScrollableTableRow>
              </ScrollableTableHeader>
              <ScrollableTableBody>
                {filteredStudents.map((student) => (
                  <ScrollableTableRow key={student.id}>
                    <ScrollableTableCell>
                      <div className="flex items-center gap-3">
                        <Avatar
                          fullName={studentDisplayName(student)}
                          cloudinaryPhotoPublicId={student.cloudinaryPhotoPublicId}
                          userId={student.id}
                          isStudent
                          size={10}
                        />
                        <div>
                          <p className="font-semibold text-gray-900">{studentDisplayName(student)}</p>
                          {hasDistinctStudentLegalName(student) && (
                            <p className="text-xs text-gray-500">{student.fullName}</p>
                          )}
                        </div>
                      </div>
                    </ScrollableTableCell>
                    <ScrollableTableCell>
                      <div className="flex flex-wrap gap-1.5">
                        {(student.classNames || []).slice(0, 2).map((className) => (
                          <Badge key={`${student.id}-${className}`} variant="outline">
                            {className}
                          </Badge>
                        ))}
                      </div>
                    </ScrollableTableCell>
                    <ScrollableTableCell>
                      <Badge variant="secondary">{student.currentSkillLevelName || 'Unassigned'}</Badge>
                    </ScrollableTableCell>
                    <ScrollableTableCell className="text-gray-900">{student.guardianEmail || '-'}</ScrollableTableCell>
                    <ScrollableTableCell>
                      <div className="flex justify-end">
                        <Button variant="outline" size="sm" onClick={() => openStudentPanel(student.id)}>
                          Manage
                        </Button>
                      </div>
                    </ScrollableTableCell>
                  </ScrollableTableRow>
                ))}
                {filteredStudents.length === 0 && (
                  <ScrollableTableRow>
                    <ScrollableTableCell colSpan={5} className="text-center py-10 text-sm text-gray-600">
                      No students found.
                    </ScrollableTableCell>
                  </ScrollableTableRow>
                )}
              </ScrollableTableBody>
            </ScrollableTable>
          </ScrollableTableContainer>

          <div className="space-y-3 lg:hidden">
            {filteredStudents.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-white px-6 py-12 text-center text-gray-500">
                No students found.
              </div>
            ) : (
              filteredStudents.map((student) => (
                <div key={student.id} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="mb-3 flex items-center gap-3">
                    <Avatar
                      fullName={studentDisplayName(student)}
                      cloudinaryPhotoPublicId={student.cloudinaryPhotoPublicId}
                      userId={student.id}
                      isStudent
                      size={12}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-gray-900">{studentDisplayName(student)}</p>
                      {hasDistinctStudentLegalName(student) && (
                        <p className="truncate text-sm text-gray-500">{student.fullName}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="mb-1 text-xs font-medium uppercase text-gray-500">Classes</p>
                      {(student.classNames || []).length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {(student.classNames || []).slice(0, 3).map((className) => (
                            <Badge key={`${student.id}-mobile-${className}`} variant="outline">
                              {className}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600">No classes</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <p className="mb-1 text-xs font-medium uppercase text-gray-500">Level</p>
                        <Badge variant="secondary">{student.currentSkillLevelName || 'Unassigned'}</Badge>
                      </div>
                      <div>
                        <p className="mb-1 text-xs font-medium uppercase text-gray-500">Guardian Email</p>
                        <p className="truncate text-sm text-gray-900">{student.guardianEmail || '-'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 border-t border-gray-200 pt-3">
                    <Button className="w-full" variant="outline" size="sm" onClick={() => openStudentPanel(student.id)}>
                      Manage
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <DrawerContent className="md:max-w-xl">
          <DrawerHeader>
            <DrawerTitle>{studentDisplayName(selectedStudent)}</DrawerTitle>
            <DrawerDescription>Update levels, skills, and add public or private notes.</DrawerDescription>
          </DrawerHeader>
          <div className="flex-1 overflow-y-auto px-4 pb-6">
            {!selectedStudent ? null : (
              <div className="space-y-6">
                <section className="space-y-3 rounded-md border border-gray-200 p-4">
                  <h2 className="text-sm font-semibold text-gray-900">Skill Level</h2>
                  <div className="flex flex-wrap gap-2">
                    {skillLevels.map((level) => {
                      const isCurrent = String(selectedStudent.currentSkillLevelId || '') === String(level.id)
                      return (
                        <Button
                          key={level.id}
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={isSavingLevel}
                          className={cn(
                            'rounded-full',
                            isCurrent ? 'bg-gray-900 text-white border-gray-900 hover:bg-gray-900 hover:text-white' : ''
                          )}
                          onClick={() => handleLevelChange(selectedStudent.id, level.id)}
                        >
                          {level.name}
                        </Button>
                      )
                    })}
                  </div>
                  {levelErrors && <p className="text-sm text-red-600">{levelErrors}</p>}
                  <p className="text-xs text-gray-600">
                    {formatUpdatedByText(selectedStudent.levelLastUpdatedBy, selectedStudent.levelLastUpdatedAt)}
                  </p>
                </section>

                <section className="space-y-3 rounded-md border border-gray-200 p-4">
                  <h2 className="text-sm font-semibold text-gray-900">Skills</h2>
                  <div className="space-y-2">
                    {(selectedStudent.studentsSkills || []).map((skill) => (
                      <div
                        key={skill.id}
                        className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white"
                      >
                        <SkillButton
                          label={skill.shortLabel}
                          state={skill.status}
                          onClick={() => handleSkillChange(skill)}
                          loading={isSkillLoading(skill.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 font-medium truncate">{skill.name}</p>
                          <p className="text-xs text-gray-500">
                            {formatUpdatedByText(skill.statusUpdatedBy, skill.statusUpdatedAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {skillErrorMessage && <p className="text-sm text-red-600">{skillErrorMessage}</p>}
                    {(selectedStudent.studentsSkills || []).length === 0 && (
                      <p className="text-sm text-gray-600">No skills available for this level.</p>
                    )}
                  </div>
                </section>

                <section className="space-y-3 rounded-md border border-gray-200 p-4">
                  <Tabs value={noteTab} onValueChange={setNoteTab} className="w-full">
                    <TabsList className="w-full">
                      <TabsTrigger value="public" className="w-full">
                        Public Notes
                      </TabsTrigger>
                      <TabsTrigger value="private" className="w-full">
                        Private Notes
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent value="public" className="mt-3 space-y-3">
                      <Textarea
                        value={visibleNoteDraft}
                        onChange={(event) =>
                          setNoteDraftsByVisibility((previous) => ({ ...previous, [noteTab]: event.target.value }))
                        }
                        placeholder="Write a public note"
                      />
                    </TabsContent>
                    <TabsContent value="private" className="mt-3 space-y-3">
                      <Textarea
                        value={visibleNoteDraft}
                        onChange={(event) =>
                          setNoteDraftsByVisibility((previous) => ({ ...previous, [noteTab]: event.target.value }))
                        }
                        placeholder="Write a private note"
                      />
                    </TabsContent>
                  </Tabs>

                  {visibleNoteError && <p className="text-sm text-red-600">{visibleNoteError}</p>}
                  <Button size="sm" onClick={saveNote} disabled={isSavingNote}>
                    {visibleEditId ? 'Update Note' : 'Add Note'}
                  </Button>

                  <div className="space-y-2">
                    {isNotesLoading && <p className="text-sm text-gray-500">Loading notes...</p>}
                    {!isNotesLoading &&
                      visibleNotes.map((note) => (
                        <div key={note.id} className="rounded-md border border-gray-200 p-3">
                          <p className="text-sm text-gray-800">{note.body}</p>
                          <p className="mt-2 text-xs text-gray-500">
                            {formatEditedBy(note.author)} {formatTimestamp(note.createdAt)}
                          </p>
                        </div>
                      ))}
                  </div>
                </section>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </ApplicationLayout>
  )
}

export default FacilitiesCoachesDashboard
